import { useMemo, useRef } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useIsNFCSContractPaused } from 'modules/contracts/hooks/useIsNFCSContractPaused'
import { useNFCSFormAddActions } from './useNFCSFormAddActions'
import { useNFCSInfo } from 'modules/nfcs/hooks/useNFCSInfo'
import { useNFCSState } from 'modules/nfcs/hooks/useNFCSState'

import { ProgressBar } from 'shared/ui/common/ProgressBar'
import { NetworkGuardWrap } from 'shared/ui/common/NetworkGuardWrap'
import { NFCSFormPartHeader } from '../NFCSFormPartHeader'
import { NFCSFormPartFooter } from '../NFCSFormPartFooter'
import { NFCSFormPartSignButton } from '../NFCSFormPartSignButton'
import { NFCSFormPartAddressesWrap } from '../NFCSFormPartAddressesWrap'
import { NFCSFormPartAddress } from '../NFCSFormPartAddress'
import { NFCSFormPartSteps } from '../NFCSFormPartSteps'
import { NFCSFormPrimaryAddressSign } from '../NFCSFormPrimaryAddressSign'
import { NFCSFormAddDescription } from '../NFCSFormAddDescription'
import { NFCSFormStep } from 'modules/nfcs/types'

type Props = {
  onTxSubmit?: (txHash: string) => void
}

export function NFCSFormAdd({ onTxSubmit }: Props) {
  const { walletAddress } = useWeb3()
  const initAddressRef = useRef<typeof walletAddress>(null)
  const { signedAddresses, nfcsStatus, currentFormStep, signs } = useNFCSState()
  const { isNFCSPaused, isLoading: isNFCSPausedLoading } =
    useIsNFCSContractPaused()

  if (!initAddressRef.current) initAddressRef.current = walletAddress
  const addFormInitAddress = initAddressRef.current

  const {
    primaryBundleAddress,
    isLoading: isLoadingInfo,
    nfcsBundle,
  } = useNFCSInfo({
    address: addFormInitAddress,
  })

  const {
    addTx,
    handleAddAddress,
    handleRemoveAddress,
    handleMintClick,
    isAddressPending,
  } = useNFCSFormAddActions({ primaryBundleAddress, onTxSubmit })

  const isCurrentAddressSigned = useMemo(() => {
    if (!walletAddress) return false
    return Boolean(
      signedAddresses.includes(walletAddress) ||
        nfcsBundle.includes(walletAddress),
    )
  }, [signedAddresses, nfcsBundle, walletAddress])

  const signedAddressDisplay = useMemo(() => {
    if (!nfcsBundle.length) return signedAddresses
    return signedAddresses.filter(addr => !nfcsBundle.includes(addr))
  }, [nfcsBundle, signedAddresses])

  const isLoading =
    isNFCSPausedLoading ||
    isAddressPending ||
    addTx.isSigning ||
    nfcsStatus === 'loading'

  const isAddressAlreadyHasToken = nfcsStatus === 'generated'
  const isDisabledSign = Boolean(
    isAddressAlreadyHasToken || isNFCSPaused || isCurrentAddressSigned,
  )

  const isDisabledSubmit =
    currentFormStep === NFCSFormStep.Start
      ? signedAddressDisplay.length < 1
      : isNFCSPaused ||
        walletAddress !== primaryBundleAddress ||
        (isAddressAlreadyHasToken && walletAddress !== primaryBundleAddress) ||
        !signs[signedAddresses[0]]

  if (isLoadingInfo) {
    return <ProgressBar isCentered />
  }

  return (
    <>
      <NFCSFormPartSteps
        stepsCount={2}
        activeStepIndex={
          currentFormStep === NFCSFormStep.Finish ? 1 : currentFormStep
        }
      />
      <NFCSFormPartHeader
        title="Add more addresses"
        subtitle="to your nfcs bundle"
      />
      <NFCSFormAddDescription />
      <NetworkGuardWrap>
        <NFCSFormPartAddressesWrap>
          {nfcsBundle.map((address, i) => (
            <NFCSFormPartAddress
              key={`bundled-${address}`}
              address={address}
              index={i}
              description={
                i === 0 && (
                  <NFCSFormPrimaryAddressSign
                    nfcsMode="update"
                    isLoading={isAddressPending}
                    onClick={handleAddAddress}
                  />
                )
              }
            />
          ))}
          {signedAddressDisplay.map((address, i) => (
            <NFCSFormPartAddress
              key={`new-${address}`}
              address={address}
              index={i + nfcsBundle.length}
              onRemove={
                currentFormStep !== NFCSFormStep.Finish
                  ? handleRemoveAddress(address)
                  : undefined
              }
            />
          ))}
        </NFCSFormPartAddressesWrap>
        <NFCSFormPartSignButton
          isLoading={isLoading}
          isDisabled={isDisabledSign}
          isCurrentAddressAdded={isCurrentAddressSigned}
          showTaken={isAddressAlreadyHasToken}
          onAddAddress={handleAddAddress}
        />
      </NetworkGuardWrap>
      {signedAddressDisplay.length > 0 && (
        <NFCSFormPartFooter
          submitText={
            currentFormStep === NFCSFormStep.Start
              ? 'Continue'
              : 'Update NFCS bundle'
          }
          estimatedGas={addTx.estimatedGas}
          isLoading={isLoading}
          isContractPaused={isNFCSPaused}
          isDisabled={isDisabledSubmit}
          shouldShowHint={false}
          onSubmit={handleMintClick}
        />
      )}
    </>
  )
}
